import { useEffect } from 'react';

function FieldControl({ field, value, onChange }) {
  const baseClass = 'mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15';

  if (field.type === 'select') {
    return (
      <select
        value={value ?? ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        className={baseClass}
        disabled={field.disabled}
      >
        {(field.options || []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        className={`${baseClass} min-h-24 resize-y`}
        placeholder={field.placeholder}
      />
    );
  }

  if (field.type === 'checkbox') {
    return (
      <label className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(field.name, e.target.checked)}
          className="h-4 w-4 accent-primary"
        />
        {field.checkLabel || field.label}
      </label>
    );
  }

  if (field.type === 'file') {
    return (
      <input
        type="file"
        accept={field.accept}
        onChange={(e) => onChange(field.name, e.target.files?.[0] || null)}
        className={`${baseClass} file:ml-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700`}
      />
    );
  }

  if (field.type === 'multicheck') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="mt-2 flex flex-wrap gap-2">
        {(field.options || []).map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                const next = active
                  ? selected.filter((item) => item !== option.value)
                  : [...selected, option.value];
                onChange(field.name, next.length ? next : [option.value]);
              }}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                active
                  ? 'border-primary bg-red-50 text-primary shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {option.color && (
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: option.color }} />
              )}
              <span>{active ? '✓ ' : ''}{option.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <input
      type={field.type || 'text'}
      value={value ?? ''}
      onChange={(e) => onChange(field.name, e.target.value)}
      className={baseClass}
      placeholder={field.placeholder}
      min={field.min}
      step={field.step}
    />
  );
}

export default function MasterEditModal({ open, title, subtitle, fields, values, onChange, onClose, onSubmit, saving }) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-white/70 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-l from-red-50 via-white to-slate-50 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-50"
            aria-label="إغلاق"
          >
            ×
          </button>
        </div>

        <div className="max-h-[62vh] overflow-y-auto px-5 py-4">
          <div className="grid gap-4 md:grid-cols-2">
            {fields.map((field) => (
              <div key={field.name} className={field.fullWidth ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-semibold text-slate-700">
                  {field.label}
                  {field.required && <span className="text-primary"> *</span>}
                </label>
                <FieldControl field={field} value={values[field.name]} onChange={onChange} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            disabled={saving}
          >
            إلغاء
          </button>
          <button
            type="submit"
            className="rounded-lg bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-60"
            disabled={saving}
          >
            {saving ? 'جاري الحفظ...' : 'حفظ التعديل'}
          </button>
        </div>
      </form>
    </div>
  );
}
